/* Phase 10 — Group Lead operational dashboard.
   Data comes from GET /api/dashboards/group-lead?view=team|employee|candidate.
   Scoped server-side to the actor's managed_teams / managed_groups.        */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  UsersFour, UserCircle, Handshake, Trophy, Target, TrendUp, ClipboardText, CheckSquare,
  ChartLineUp, Fire, EnvelopeSimple, Calendar,
} from "@phosphor-icons/react";

const KPIS = [
  ["total_candidates", "Total Candidates", UserCircle, "sky", "/candidates"],
  ["active_candidates", "Active Candidates", UserCircle, "emerald", "/candidates?status=Active"],
  ["cp", "Career Portal", TrendUp, "sky", "/dashboard"],
  ["ea", "Easy Apply", TrendUp, "amber", "/dashboard"],
  ["cold_emails", "Cold Emails", EnvelopeSimple, "violet", "/cold-emails"],
  ["interviews_scheduled", "Interviews Scheduled", Calendar, "sky", "/interviews"],
  ["interviews_confirmed", "Interviews Confirmed", CheckSquare, "violet", "/interviews"],
  ["interviews_completed", "Interviews Completed", ClipboardText, "slate", "/interviews"],
  ["interviews_selected", "Interviews Selected", Handshake, "emerald", "/interviews"],
  ["offers", "Offers", Trophy, "amber", "/interviews"],
  ["placements", "Placements", Trophy, "emerald", "/candidates?status=Placed"],
  ["pending_interview_reviews", "Pending Interview Reviews", CheckSquare, "orange", "/approvals?status=Pending%20Team%20Leader%20Review&type=interview_confirmed"],
  ["pending_offer_reviews", "Pending Offer Reviews", CheckSquare, "orange", "/approvals?status=Pending%20Team%20Leader%20Review&type=placement"],
  ["pending_hr_approvals", "Pending HR Approvals", CheckSquare, "indigo", "/approvals?status=Pending%20HR%20Approval"],
  ["assigned_today", "Assigned Today", ChartLineUp, "sky", "/assignments"],
  ["reassigned_today", "Reassigned Today", ChartLineUp, "amber", "/assignments"],
  ["applications_today", "Applications Today", Fire, "rose", "/dashboard"],
  ["total_executives", "Executives", UsersFour, "slate", "/employees"],
  ["total_team_leaders", "Team Leaders", UsersFour, "slate", "/employees"],
];

function Kpi({ label, value, icon: Icon, tone, to, testid }) {
  return (
    <Link to={to} data-testid={testid}
          className="stat-card block hover:border-sky-300 hover:shadow-sm transition">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-2xl font-semibold mt-1 text-slate-900">{value ?? 0}</div>
        </div>
        <div className={`w-9 h-9 rounded-md grid place-items-center bg-${tone}-50 text-${tone}-600`}>
          <Icon size={18} />
        </div>
      </div>
    </Link>
  );
}

export default function GroupLeadDashboard() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("team");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    api.get(`/dashboards/group-lead?view=${view}`)
      .then((r) => setData(r.data))
      .finally(() => setBusy(false));
  }, [view]);
  const k = data?.kpis || {};

  return (
    <section data-testid="group-lead-dashboard" className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Operational Dashboard</h2>
          <p className="text-sm text-slate-500">
            Scope: <b>{(data?.scope?.teams || []).join(", ") || "All"}</b> · Members: <b>{data?.scope?.members ?? 0}</b>
          </p>
        </div>
        <Link to="/approvals" className="text-sm bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-md"
              data-testid="gl-jump-approvals">
          Open Review & Approvals →
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {KPIS.map(([key, label, icon, tone, to]) => (
          <Kpi key={key} label={label} value={k[key]} icon={icon} tone={tone} to={to}
               testid={`gl-kpi-${key}`} />
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="border-b border-slate-100 p-3 flex items-center gap-3">
          <div className="text-sm font-semibold">Breakdown</div>
          <div className="ml-auto flex gap-1">
            {[["team", "Team-wise"], ["employee", "Employee-wise"], ["candidate", "Candidate-wise"]].map(([k2, l]) => (
              <button key={k2} onClick={() => setView(k2)} data-testid={`gl-view-${k2}`}
                      className={`text-xs px-3 py-1.5 rounded-full border ${view === k2 ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          {busy && <div className="p-6 text-center text-slate-400">Loading…</div>}
          {!busy && view === "team" && <TeamTable rows={data?.rows || []} />}
          {!busy && view === "employee" && <EmployeeTable rows={data?.rows || []} />}
          {!busy && view === "candidate" && <CandidateTable rows={data?.rows || []} />}
        </div>
      </div>
    </section>
  );
}

function TeamTable({ rows }) {
  return (
    <table className="w-full text-sm" data-testid="gl-team-table">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
        <tr>
          <th className="text-left px-3 py-2">Team</th>
          <th className="text-right px-3 py-2">Executives</th>
          <th className="text-right px-3 py-2">Active</th>
          <th className="text-right px-3 py-2">Interviews</th>
          <th className="text-right px-3 py-2">Offers</th>
          <th className="text-right px-3 py-2">Placements</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 p-6">No teams in scope.</td></tr>}
        {rows.map((r) => (
          <tr key={r.team} className="border-t border-slate-100" data-testid={`gl-team-row-${r.team}`}>
            <td className="px-3 py-2 font-medium">{r.team}</td>
            <td className="px-3 py-2 text-right">{r.executives}</td>
            <td className="px-3 py-2 text-right">{r.active_candidates}</td>
            <td className="px-3 py-2 text-right">{r.interviews}</td>
            <td className="px-3 py-2 text-right">{r.offers}</td>
            <td className="px-3 py-2 text-right">{r.placements}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function EmployeeTable({ rows }) {
  return (
    <table className="w-full text-sm" data-testid="gl-employee-table">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
        <tr>
          <th className="text-left px-3 py-2">Employee</th>
          <th className="text-left px-3 py-2">Team</th>
          <th className="text-right px-3 py-2">Active</th>
          <th className="text-right px-3 py-2">Confirmed IVs</th>
          <th className="text-right px-3 py-2">Placements</th>
          <th className="text-right px-3 py-2">Apps Today</th>
          <th className="text-right px-3 py-2">Daily Target</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 p-6">No members in scope.</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.employee_id || `emp-${i}`} className="border-t border-slate-100" data-testid={`gl-emp-row-${r.employee_id || i}`}>
            <td className="px-3 py-2 font-medium">{r.name} <span className="text-xs text-slate-400">({r.role})</span></td>
            <td className="px-3 py-2 text-slate-500">{r.team || "—"}</td>
            <td className="px-3 py-2 text-right">{r.active_candidates}</td>
            <td className="px-3 py-2 text-right">{r.interviews_confirmed}</td>
            <td className="px-3 py-2 text-right">{r.placements}</td>
            <td className="px-3 py-2 text-right">{r.applications_today}</td>
            <td className="px-3 py-2 text-right">{r.daily_target}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function CandidateTable({ rows }) {
  return (
    <table className="w-full text-sm" data-testid="gl-candidate-table">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
        <tr>
          <th className="text-left px-3 py-2">Candidate</th>
          <th className="text-left px-3 py-2">Owner</th>
          <th className="text-left px-3 py-2">Placement</th>
          <th className="text-left px-3 py-2">Status</th>
          <th className="text-left px-3 py-2">Priority</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 p-6">No candidates in scope.</td></tr>}
        {rows.map((c, i) => (
          <tr key={c.id || `row-${i}`} className="border-t border-slate-100">
            <td className="px-3 py-2 font-medium"><Link to={`/candidates/${c.id}`} className="text-sky-700 hover:underline">{c.full_name}</Link></td>
            <td className="px-3 py-2 text-slate-500">{c.assigned_employee_name || "—"}</td>
            <td className="px-3 py-2 text-slate-500">{c.placement_field || "—"}</td>
            <td className="px-3 py-2 text-slate-500">{c.service_status || "—"}</td>
            <td className="px-3 py-2 text-slate-500">{c.priority || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
